import type { RaceHistoryDetail } from '@multirx/shared/types/raceHistory.js';
import {
  resolveRowsForRound,
  rankingAtRound,
  resolveBoardAfterRound,
} from '@multirx/shared/game/archiveReplay.js';
import { isOwnDriver } from '@multirx/shared/game/driverDisplayLabel.js';
import { getOwnDriverId, labelOwnDriver } from '../auth/ownDriverLabel.js';
import { nationalityFlagMarkup } from '../components/nationalityFlag.js';
import { resolveMediaUrl } from '../mediaUrl.js';
import { buildPlayerProfileTooltipHtml, displayLapCurrent } from '../game/playerProfileTooltip.js';

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}

const ROSTER_TOOLTIP_GAP = 10;
let rosterTooltipEl: HTMLDivElement | null = null;
let rosterTooltipAnchor: HTMLElement | null = null;

function getRosterTooltipEl(): HTMLDivElement {
  if (!rosterTooltipEl) {
    rosterTooltipEl = document.createElement('div');
    rosterTooltipEl.className = 'archives-replay-roster-tooltip race-profile-tooltip';
    rosterTooltipEl.hidden = true;
    document.body.appendChild(rosterTooltipEl);
  }
  return rosterTooltipEl;
}

function positionRosterTooltip(tip: HTMLDivElement, anchor: HTMLElement): void {
  tip.hidden = false;
  const anchorRect = anchor.getBoundingClientRect();
  const tipRect = tip.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const pad = 8;

  let left = anchorRect.left - tipRect.width - ROSTER_TOOLTIP_GAP;
  let top = anchorRect.top + anchorRect.height / 2 - tipRect.height / 2;

  if (left < pad) {
    left = anchorRect.left + anchorRect.width / 2 - tipRect.width / 2;
    top = anchorRect.top - tipRect.height - ROSTER_TOOLTIP_GAP;
    tip.classList.add('is-below-anchor');
  } else {
    tip.classList.remove('is-below-anchor');
  }

  left = Math.max(pad, Math.min(left, vw - tipRect.width - pad));
  top = Math.max(pad, Math.min(top, vh - tipRect.height - pad));

  tip.style.left = `${Math.round(left)}px`;
  tip.style.top = `${Math.round(top)}px`;
}

function showRosterTooltip(anchor: HTMLElement, html: string): void {
  const tip = getRosterTooltipEl();
  rosterTooltipAnchor = anchor;
  tip.innerHTML = html;
  tip.hidden = false;
  requestAnimationFrame(() => {
    if (rosterTooltipAnchor === anchor) positionRosterTooltip(tip, anchor);
  });
}

function hideRosterTooltip(): void {
  rosterTooltipAnchor = null;
  if (rosterTooltipEl) rosterTooltipEl.hidden = true;
}

/** Retire le tooltip flottant (changement de page / démontage archives). */
export function removeArchiveRosterTooltip(): void {
  hideRosterTooltip();
  rosterTooltipEl?.remove();
  rosterTooltipEl = null;
}

export function buildArchiveParticipantTooltip(
  detail: RaceHistoryDetail,
  playerIndex: number,
  round: number
): string {
  const p = detail.replay.participants.find((x) => x.playerIndex === playerIndex);
  const board = resolveBoardAfterRound(detail.replay, round, detail.numParticipants);
  const snap = board.find((s) => s.playerIndex === playerIndex);
  const rank =
    rankingAtRound(detail.replay, round, detail.numParticipants).find(
      (r) => r.playerIndex === playerIndex
    )?.rank ?? playerIndex + 1;
  const finished = snap?.finished ?? false;
  const lapCurrent = displayLapCurrent(snap?.lapCount ?? 0, detail.numLaps, finished);
  const bonuses = p?.raceBonuses;
  const stats = bonuses
    ? [
        { value: `+${bonuses.straightLine}`, label: 'Ligne' },
        { value: `+${bonuses.corner}`, label: 'Virage' },
        { value: `+${bonuses.start}`, label: 'Départ' },
        { value: `+${bonuses.combativeness}`, label: 'Défense' },
      ]
    : p?.engine != null && p?.tire != null
      ? [
          { value: `★${p.engine}`, label: 'Moteur' },
          { value: `★${p.tire}`, label: 'Pneu' },
        ]
      : [{ value: '—', label: 'Stats non enregistrées' }];

  const showCar = Boolean(p?.carName) && p!.carName !== '—';
  const handle =
    p?.division != null
      ? `DIVISION D${p.division}`
      : showCar
        ? 'VOITURE DE COURSE'
        : `PILOTE · SLOT ${playerIndex + 1}`;

  return buildPlayerProfileTooltipHtml({
    driverName: p?.driverName ?? `Pilote ${playerIndex + 1}`,
    carName: showCar ? p!.carName : null,
    driverImagePath: p?.driverImagePath ?? null,
    driverDivision: p?.division,
    nationalityCode: p?.nationality,
    isOwnDriver: isOwnDriver(p?.driverId, getOwnDriverId()),
    rank,
    statusLabel: finished ? 'TERMINÉ' : `TOUR ${lapCurrent}/${detail.numLaps}`,
    statusVariant: finished ? 'finished' : 'racing',
    handle,
    bio: null,
    stats,
    footer: showCar ? p!.carName.toUpperCase() : null,
  });
}

export type ArchiveRosterOptions = {
  /** Pilote suivi par la caméra (surlignage). */
  followedPlayerIndex?: number | null;
  /** Clic → centrer la caméra 3D sur ce pilote. */
  onSelectPlayer?: (playerIndex: number) => void;
};

export function renderArchiveRoster(
  container: HTMLElement,
  detail: RaceHistoryDetail,
  round: number,
  options?: ArchiveRosterOptions
): void {
  const ranking = rankingAtRound(detail.replay, round, detail.numParticipants);
  const board = resolveBoardAfterRound(detail.replay, round, detail.numParticipants);
  const roundRows = resolveRowsForRound(detail.replay, round, detail.numParticipants);

  hideRosterTooltip();
  container.innerHTML = '';
  const head = document.createElement('div');
  head.className = 'archives-replay-roster-head';
  head.innerHTML = `<span class="archives-replay-roster-title">Pilotes</span><span class="archives-replay-roster-turn">Tour ${round}</span>`;
  container.appendChild(head);

  const list = document.createElement('div');
  list.className = 'archives-replay-roster-list';

  for (const { playerIndex, rank } of ranking) {
    const p = detail.replay.participants.find((x) => x.playerIndex === playerIndex);
    const snap = board.find((s) => s.playerIndex === playerIndex);
    const rowEntry = roundRows.find((r) => r.playerIndex === playerIndex);
    const flag = p?.nationality
      ? nationalityFlagMarkup(p.nationality, { className: 'archives-replay-roster-flag' })
      : '';
    const finished = snap?.finished ?? false;
    const roll = rowEntry?.roll != null && rowEntry.roll > 0 ? String(rowEntry.roll) : null;
    const move =
      rowEntry?.effectiveMove != null && rowEntry.effectiveMove > 0
        ? String(rowEntry.effectiveMove)
        : null;
    const diceBit =
      roll != null
        ? `<span class="archives-replay-roster-dice">${roll}${move != null ? ` (${move})` : ''}</span>`
        : '';
    const followed = options?.followedPlayerIndex === playerIndex;

    const row = document.createElement('button');
    row.type = 'button';
    row.className = `archives-replay-roster-row${finished ? ' is-finished' : ''}${followed ? ' is-followed' : ''}`;
    row.dataset.playerIndex = String(playerIndex);
    row.title = 'Centrer la caméra sur ce pilote';
    const carThumb = p?.carImagePath
      ? `<img class="archives-replay-roster-car" src="${escapeHtml(resolveMediaUrl(p.carImagePath))}" alt="" loading="lazy" />`
      : `<span class="archives-replay-roster-car archives-replay-roster-car--placeholder" aria-hidden="true"></span>`;
    row.innerHTML = `
      <span class="archives-replay-roster-pos">${rank}.</span>
      ${carThumb}
      <span class="archives-replay-roster-meta">
        <span class="archives-replay-roster-name">${flag} ${escapeHtml(labelOwnDriver(p?.driverName ?? `Pilote ${playerIndex + 1}`, p?.driverId))}</span>
        <span class="archives-replay-roster-car-name">${escapeHtml(p?.carName ?? '')}</span>
      </span>
      ${diceBit}
    `;

    const tooltipHtml = buildArchiveParticipantTooltip(detail, playerIndex, round);
    const showTip = (): void => showRosterTooltip(row, tooltipHtml);
    const hideTip = (): void => {
      if (rosterTooltipAnchor === row) hideRosterTooltip();
    };
    row.addEventListener('mouseenter', showTip);
    row.addEventListener('focus', showTip);
    row.addEventListener('mouseleave', hideTip);
    row.addEventListener('blur', hideTip);
    row.addEventListener('click', (): void => {
      options?.onSelectPlayer?.(playerIndex);
    });

    list.appendChild(row);
  }

  container.appendChild(list);
}
